import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Select from '../ReactSelect/ReactSelect';
import { FormGroup, Input, InputNumber } from 'ps-design';
import './SelectableInput.scss';
import { DatePickerInput } from '@mantine/dates';
import classNames from 'classnames';
import moment from 'moment';

export const INPUT_TYPE = {
  TEXT: 'text',
  NUMERIC: 'numeric',
  DATE: 'date'
};

class SelectableInput extends Component {
  static propTypes = {
    name: PropTypes.string,
    classNamePrefix: PropTypes.string,
    className: PropTypes.string,
    options: PropTypes.array,
    getOptionValue: PropTypes.func,
    getOptionLabel: PropTypes.func,
    getOptionType: PropTypes.func,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    noOptionsMessage: PropTypes.func,
    instanceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    openMenuOnFocus: PropTypes.bool,
    autoFocus: PropTypes.bool,
    onBlur: PropTypes.func,
    isDisabled: PropTypes.bool,
    inputClassName: PropTypes.string,
    inputContainerClassName: PropTypes.string,
    labelClassName: PropTypes.string,
    inputValueOptionProperty: PropTypes.string,
    selectedOptions: PropTypes.array
  };

  static defaultProps = {
    selectedOptions: []
  };

  constructor(props) {
    super(props);

    this.state = {
      selectedOptions: this.props.selectedOptions,
      datePickerFocus: {}
    };

    this.optionOnChange = this.optionOnChange.bind(this);
  }

  componentWillUnmount() {
    this.clearOptionValues();
    this.setState({ selectedOptions: [] });
  }

  renderInputs() {
    const {
      getOptionLabel,
      getOptionType,
      labelClassName,
      inputClassName,
      inputContainerClassName,
      isDisabled
    } = this.props;
    return this.state.selectedOptions.map(option => {
      const optionValue = this.getOptionValueProperty(option);
      const optionLabel = getOptionLabel ? getOptionLabel(option) : option.label;
      const inputContainerClassNames = classNames('selectable-input__input-container', inputContainerClassName);
      const inputClassNames = classNames('selectable-input__input', inputClassName);
      const labelClassNames = classNames('selectable-input__label', labelClassName);
      const inputValueProperty = this.getInputValueOptionProperty();
      let inputType = INPUT_TYPE.TEXT;
      if (getOptionType) {
        let type = getOptionType(option);
        if (Object.values(INPUT_TYPE).includes(type)) {
          inputType = type;
        }
      }

      const inputGroupClassNames = classNames('selectable-input__input-group', {
        'input-number': inputType === INPUT_TYPE.NUMERIC
      });

      return (
        <div className={inputContainerClassNames} key={optionValue}>
          <FormGroup text={optionLabel} labelClassName={labelClassNames}>
            <div className={inputGroupClassNames}>
              {inputType === INPUT_TYPE.TEXT && (
                <Input
                  className={inputClassNames}
                  value={option[inputValueProperty]}
                  onChange={this.setOptionValue.bind(this, option)}
                  disabled={isDisabled}
                />
              )}
              {inputType === INPUT_TYPE.NUMERIC && (
                <InputNumber
                  className={inputClassNames}
                  value={option[inputValueProperty]}
                  onChange={this.setOptionValue.bind(this, option)}
                  disabled={isDisabled}
                />
              )}
              {inputType === INPUT_TYPE.DATE && (
                <DatePickerInput
                  size="xl"
                  value={option[inputValueProperty] ? new Date(option[inputValueProperty]) : option[inputValueProperty]}
                  onChange={this.setOptionDateValue.bind(this, option)}
                />
              )}
              {!isDisabled && <i onClick={() => this.removeOption(option)} className="icon-trash" />}
            </div>
          </FormGroup>
        </div>
      );
    });
  }

  isOutsideRange() {
    return false;
  }

  setOptionDateValue(option, value) {
    const { selectedOptions } = this.state;
    const { onChange } = this.props;
    option[this.getInputValueOptionProperty()] = value;
    this.setState({ selectedOptions });
    if (onChange) {
      onChange(option, selectedOptions);
    }
  }

  handleDatePickerFocus(option, { focused }) {
    const { datePickerFocus } = this.state;
    const optionValueProperty = this.getOptionValueProperty(option);
    datePickerFocus[optionValueProperty] = focused;
    this.setState({ datePickerFocus });
  }

  getOptionValueProperty(option) {
    const { getOptionValue } = this.props;
    return getOptionValue ? getOptionValue(option) : option.value;
  }

  setOptionValue(option, id, value) {
    const { selectedOptions } = this.state;
    const { onChange } = this.props;
    option[this.getInputValueOptionProperty()] = value;
    this.setState({ selectedOptions });
    if (onChange) {
      onChange(option, selectedOptions);
    }
  }

  clearOptionValues() {
    const { options } = this.props;
    if (options) {
      options.forEach(option => {
        delete option[this.getInputValueOptionProperty()];
      });
    }
  }

  getInputValueOptionProperty() {
    const { inputValueOptionProperty } = this.props;
    return inputValueOptionProperty || 'inputValue';
  }

  removeOption(optionToRemove) {
    const { selectedOptions } = this.state;
    const { onChange } = this.props;
    const newSelectedOptions = selectedOptions.filter(option => option !== optionToRemove);
    delete optionToRemove[this.getInputValueOptionProperty()];
    this.setState({ selectedOptions: newSelectedOptions });
    if (onChange) {
      onChange(optionToRemove, newSelectedOptions);
    }
  }

  optionOnChange(option) {
    const { selectedOptions } = this.state;
    const { onChange } = this.props;
    selectedOptions.push(option);
    this.setState({ selectedOptions });
    if (onChange) {
      onChange(option, selectedOptions);
    }
  }

  render() {
    const { selectedOptions } = this.state;
    const { options, className, isDisabled } = this.props;
    const selectClassNames = classNames('selectable-input__select', className);
    return (
      <React.Fragment>
        {this.renderInputs()}
        {!isDisabled && (
          <Select
            {...this.props}
            className={selectClassNames}
            onChange={this.optionOnChange}
            options={options.filter(option => {
              const optionValue = this.getOptionValueProperty(option);
              const found = selectedOptions.find(
                selectedOption => this.getOptionValueProperty(selectedOption) === optionValue
              );
              return !found;
            })}
            value={null}
          />
        )}
      </React.Fragment>
    );
  }
}

export default SelectableInput;
